export type Features = {
  'Platform pricing': {
    'Base price': string;
    'Included MAUs': string;
    'Additional MAUs': string;
    'MAU cap': string;
    'Included MAOs': string;
    'Additional MAOs': string;
    'Allowed Clerk collaborators': string;
    'Additional Clerk collaborators': string;
    title: string;
  };
  'Messaging pricing': {
    'Verification emails': string;
    'Invitation emails': string;
    'Transactional emails': string | null;
    'Verification SMSs': string | null;
    'Transactional SMSs': string | null;
    title: string;
  };
  'UI & Branding': {
    'Hosted pages': boolean;
    'Pre-built components': boolean;
    'CSS customization': boolean;
    'Build your own UIs': boolean;
    'Custom domain': boolean;
    'Remove Clerk branding': boolean;
    title: string;
  };
  'Authentication features': {
    title: string;
    Passwords: boolean;
    'Password complexity requirements': boolean;
    'Magic links / Email codes': boolean;
    'SMS phone codes': boolean;
    'Social logins / Web3 wallets': string;
    'Custom auth methods': boolean;
    'Allowlist/blocklist': boolean;
    'SMS multifactor auth': boolean;
    'TOTP multifactor auth': boolean;
    SAML: boolean;
  };
  'User management': {
    'Admin dashboard': boolean;
    'Account linking': boolean;
    'User bans': boolean;
    'User impersonation': boolean;
    title: string;
  };
  'Session features': {
    'Revocable sessions': boolean;
    'Custom JWT SSO templates': boolean;
    'Custom session tokens': boolean;
    'Custom session duration': boolean;
    'User switching': boolean;
    'Device management': boolean;
    title: string;
  };
  'Organizations features': {
    'Allowed users per org': string;
    'Invitation emails': boolean;
    'Basic RBAC': boolean;
    'Custom Roles': boolean;
    title: string;
  };
  'Support & Compliance': {
    'Exportable user data': boolean;
    'Community Support': boolean;
    'Priority support': boolean;
    'SOC2 Report': boolean;
    'HIPAA Report / BAA': boolean;
    title: string;
  };
};

export type Tiers = 'Free' | 'Hobby' | 'Business' | 'Enterprise';

export type Plan = {
  name: Tiers;
  description: string;
  features: Features;
};

type Plans = {
  free: Plan;
  hobby: Plan;
  business: Plan;
  enterprise: Plan;
};

export const ToolTips = [
  {
    name: 'Monthly active users',
    tooltip: "A user is 'active' if they visit your application."
  },
  {
    name: 'Monthly active organizations',
    tooltip:
      "An organization is 'active' if there are at least 2 members, and at least one of those members is active."
  },
  {
    name: 'Verification SMSs',
    tooltip:
      'SMS rates depend on the country.  Our SMS vendor is Twilio, and we passthrough their fees.'
  },
  {
    name: 'Hosted pages',
    tooltip: `To quickly add authentication to your app, leverage <a href="/docs/authentication/using-clerk-hosted-pages" target="_blank" rel="noopener">Clerk's hosted pages</a>.`
  },
  {
    name: 'Build your own UIs',
    tooltip: `For completely custom UIs, leverage <a href="/docs/components/customization/overview" target="_blank" rel="noopener">Clerk's powerful APIs</a>.`
  },
  {
    name: 'Custom domain',
    tooltip: 'Deploy hosted pages and send emails from your own domain.'
  },
  {
    name: 'Remove Clerk branding',
    tooltip:
      "Remove the 'Secured by Clerk' branding in Clerk's pre-built components & hosted pages."
  },
  {
    name: 'Custom auth methods',
    tooltip: 'Build your own authentication methods.'
  },
  {
    name: 'SMS Multifactor auth',
    tooltip: 'Allow users to add a phone as a second factor.'
  },
  {
    name: 'TOTP Multifactor auth',
    tooltip: 'Allow users to add a TOTP device for enhanced security.'
  },
  {
    name: 'Account linking',
    tooltip:
      'Prevent duplicate accounts when the same user signs in with a new strategy. (e.g. social sign-in after signing up by email).'
  },
  {
    name: 'Custom JWT SSO templates',
    tooltip:
      'Generate dynamic JWTs directly from the frontend and connect via JWT SSO to Hasura, Supabase, etc.'
  },
  {
    name: 'Allowlist/blocklist',
    tooltip: 'Only allow certain people to access your application.'
  },
  {
    name: 'SMS multifactor auth',
    tooltip: 'Also supports backup codes.'
  },
  {
    name: 'TOTP multifactor auth',
    tooltip: 'Also supports backup codes.'
  },
  {
    name: 'User switching',
    tooltip:
      'Allow users to sign into multiple accounts simultaneously and switch between them.'
  },
  {
    name: 'Device management',
    tooltip:
      'Allow users to manage sessions across devices and sign out remotely.'
  },
  {
    name: 'Basic RBAC',
    tooltip: "Users can either be an 'Admin' or a 'Member'."
  },
  { name: 'Custom RBAC', tooltip: 'Define your own roles and permissions.' },
  {
    name: 'Exportable user data',
    tooltip: 'Export user data, including the bcrypt hash of user passwords.'
  },
  {
    name: 'Password complexity requirements',
    tooltip:
      'Add additional requirements for passwords to include numbers and uppercase, lowercase, and special characters.'
  },
  {
    name: 'User impersonation',
    tooltip: 'Sign in as your users for support and debugging.'
  }
];

export const comingSoon = [
  {
    name: 'Custom Roles',
    text: `Q4 2023`
  },
  {
    name: 'SAML',
    text: `Beta`
  }
];

export const plans: Plans = {
  free: {
    name: 'Free',
    description:
      'Unlimited sign-ins, no credit card required. Limit 5,000 monthly active users.',
    features: {
      'Platform pricing': {
        title: 'Platform pricing',

        'Base price': 'Free',
        'Included MAUs': '-',
        'Additional MAUs': '-',
        'MAU cap': '5,000',
        'Included MAOs': '10',
        'Additional MAOs': '-',
        'Allowed Clerk collaborators': '3',
        'Additional Clerk collaborators': '$20 each'
      },
      'Messaging pricing': {
        title: 'Messaging pricing',

        'Verification emails': 'Included',
        'Invitation emails': 'Included',
        'Transactional emails': null,
        'Verification SMSs': null,
        'Transactional SMSs': null
      },
      'UI & Branding': {
        title: 'UI & Branding',

        'Hosted pages': true,
        'Pre-built components': true,
        'CSS customization': true,
        'Build your own UIs': true,
        'Custom domain': true,
        'Remove Clerk branding': false
      },
      'Authentication features': {
        title: 'Authentication features',

        Passwords: true,
        'Password complexity requirements': false,
        'Magic links / Email codes': true,
        'SMS phone codes': false,
        'Social logins / Web3 wallets': 'Up to 3',
        'Custom auth methods': true,
        'Allowlist/blocklist': false,
        'SMS multifactor auth': false,
        'TOTP multifactor auth': false,
        SAML: false
      },
      'User management': {
        title: 'User management',

        'Admin dashboard': true,
        'Account linking': true,
        'User bans': false,
        'User impersonation': false
      },
      'Session features': {
        title: 'Session features',

        'Revocable sessions': true,
        'Custom JWT SSO templates': true,
        'Custom session tokens': true,
        'Custom session duration': false,
        'User switching': false,
        'Device management': false
      },
      'Organizations features': {
        title: 'Organizations features',

        'Allowed users per org': '3',
        'Invitation emails': true,
        'Basic RBAC': true,
        'Custom Roles': false
      },
      'Support & Compliance': {
        title: 'Support & Compliance',

        'Exportable user data': true,
        'Community Support': true,
        'Priority support': false,
        'SOC2 Report': false,
        'HIPAA Report / BAA': false
      }
    }
  },
  hobby: {
    name: 'Hobby',
    description: 'Essential features for launching or growing any application.',

    features: {
      'Platform pricing': {
        title: 'Platform pricing',

        'Base price': '$25',
        'Included MAUs': '1,000',
        'Additional MAUs': '$0.02 each',
        'MAU cap': 'Unlimited',
        'Included MAOs': '100',
        'Additional MAOs': '$0.25 each',
        'Allowed Clerk collaborators': '3',
        'Additional Clerk collaborators': '$20 each'
      },

      'Messaging pricing': {
        title: 'Messaging pricing',
        'Verification emails': 'Included',
        'Invitation emails': 'Included',
        'Transactional emails': '$0.001 each',
        'Verification SMSs': 'Market rate',
        'Transactional SMSs': 'Market rate'
      },
      'UI & Branding': {
        title: 'UI & Branding',
        'Hosted pages': true,
        'Pre-built components': true,
        'CSS customization': true,
        'Build your own UIs': true,
        'Custom domain': true,
        'Remove Clerk branding': true
      },
      'Authentication features': {
        title: 'Authentication features',
        Passwords: true,
        'Password complexity requirements': false,
        'Magic links / Email codes': true,
        'SMS phone codes': true,
        'Social logins / Web3 wallets': 'Unlimited',
        'Custom auth methods': true,
        'Allowlist/blocklist': true,
        'SMS multifactor auth': false,
        'TOTP multifactor auth': false,
        SAML: false
      },
      'User management': {
        title: 'User management',

        'Admin dashboard': true,
        'Account linking': true,
        'User bans': false,
        'User impersonation': false
      },
      'Session features': {
        title: 'Session features',

        'Revocable sessions': true,
        'Custom JWT SSO templates': true,
        'Custom session tokens': true,
        'Custom session duration': false,
        'User switching': false,
        'Device management': false
      },
      'Organizations features': {
        title: 'Organizations features',

        'Allowed users per org': '10',
        'Invitation emails': true,
        'Basic RBAC': true,
        'Custom Roles': false
      },
      'Support & Compliance': {
        title: 'Support & Compliance',

        'Exportable user data': true,
        'Community Support': true,
        'Priority support': false,
        'SOC2 Report': false,
        'HIPAA Report / BAA': false
      }
    }
  },
  business: {
    name: 'Business',
    description: 'Everything you need for a growing business.',
    features: {
      'Platform pricing': {
        title: 'Platform pricing',

        'Base price': '$99',
        'Included MAUs': '1,000',
        'Additional MAUs': '$0.05 each',
        'MAU cap': 'Unlimited',
        'Included MAOs': '100',
        'Additional MAOs': '$1 each',
        'Allowed Clerk collaborators': '3',
        'Additional Clerk collaborators': '$20 each'
      },
      'Messaging pricing': {
        title: 'Messaging pricing',

        'Verification emails': 'Included',
        'Invitation emails': 'Included',
        'Transactional emails': '$0.001 each',
        'Verification SMSs': 'Market rate',
        'Transactional SMSs': 'Market rate'
      },
      'UI & Branding': {
        title: 'UI & Branding',

        'Hosted pages': true,
        'Pre-built components': true,
        'CSS customization': true,
        'Build your own UIs': true,
        'Custom domain': true,
        'Remove Clerk branding': true
      },
      'Authentication features': {
        title: 'Authentication features',

        Passwords: true,
        'Password complexity requirements': true,
        'Magic links / Email codes': true,
        'SMS phone codes': true,
        'Social logins / Web3 wallets': 'Unlimited',
        'Custom auth methods': true,
        'Allowlist/blocklist': true,
        'SMS multifactor auth': true,
        'TOTP multifactor auth': true,
        SAML: true
      },
      'User management': {
        title: 'User management',

        'Admin dashboard': true,
        'Account linking': true,
        'User bans': true,
        'User impersonation': true
      },
      'Session features': {
        title: 'Session features',

        'Revocable sessions': true,
        'Custom JWT SSO templates': true,
        'Custom session tokens': true,
        'Custom session duration': true,
        'User switching': true,
        'Device management': true
      },
      'Organizations features': {
        title: 'Organizations features',

        'Allowed users per org': 'Unlimited',
        'Invitation emails': true,
        'Basic RBAC': true,
        'Custom Roles': false
      },
      'Support & Compliance': {
        title: 'Support & Compliance',

        'Exportable user data': true,
        'Community Support': true,
        'Priority support': true,
        'SOC2 Report': true,
        'HIPAA Report / BAA': false
      }
    }
  },
  enterprise: {
    name: 'Enterprise',
    description: 'Bulk discounts, custom contracts and enterprise support.',
    features: {
      'Platform pricing': {
        title: 'Platform pricing',
        'Base price': 'Contact sales',
        'Included MAUs': 'Contact sales',
        'Additional MAUs': 'Volume discounts',
        'MAU cap': 'Unlimited',
        'Included MAOs': 'Contact sales',
        'Additional MAOs': 'Volume discounts',
        'Allowed Clerk collaborators': '3',
        'Additional Clerk collaborators': '$20 each'
      },
      'Messaging pricing': {
        title: 'Messaging pricing',
        'Verification emails': 'Included',
        'Invitation emails': 'Included',
        'Transactional emails': '$0.001 each',
        'Verification SMSs': 'Market rate',
        'Transactional SMSs': 'Market rate'
      },
      'UI & Branding': {
        title: 'UI & Branding',
        'Hosted pages': true,
        'Pre-built components': true,
        'CSS customization': true,
        'Build your own UIs': true,
        'Custom domain': true,
        'Remove Clerk branding': true
      },
      'Authentication features': {
        title: 'Authentication features',
        Passwords: true,
        'Password complexity requirements': true,
        'Magic links / Email codes': true,
        'SMS phone codes': true,
        'Social logins / Web3 wallets': 'Unlimited',
        'Custom auth methods': true,
        'Allowlist/blocklist': true,
        'SMS multifactor auth': true,
        'TOTP multifactor auth': true,
        SAML: true
      },
      'User management': {
        title: 'User management',

        'Admin dashboard': true,
        'Account linking': true,
        'User bans': true,
        'User impersonation': true
      },
      'Session features': {
        title: 'Session features',

        'Revocable sessions': true,
        'Custom JWT SSO templates': true,
        'Custom session tokens': true,
        'Custom session duration': true,
        'User switching': true,
        'Device management': true
      },
      'Organizations features': {
        title: 'Organizations features',

        'Allowed users per org': 'Unlimited',
        'Invitation emails': true,
        'Basic RBAC': true,
        'Custom Roles': false
      },
      'Support & Compliance': {
        title: 'Support & Compliance',

        'Exportable user data': true,
        'Community Support': true,
        'Priority support': true,
        'SOC2 Report': true,
        'HIPAA Report / BAA': true
      }
    }
  }
} as const;
